import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EditPromptModal = ({ prompt, isOpen, onClose, onSave }) => {
  const [editedPrompt, setEditedPrompt] = useState({
    content: '',
    category: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const categoryOptions = [
    { value: 'marketing', label: 'Marketing' },
    { value: 'legal', label: 'Legal' },
    { value: 'technical', label: 'Technical' },
    { value: 'creative', label: 'Creative' },
    { value: 'business', label: 'Business' }
  ];

  useEffect(() => {
    if (prompt) {
      setEditedPrompt({
        content: prompt?.content || '',
        category: prompt?.category || '',
        tags: prompt?.tags || []
      });
    }
  }, [prompt]);

  const handleSave = async () => {
    if (!editedPrompt?.content?.trim()) return;
    
    setIsSaving(true);
    try {
      await onSave({
        ...prompt,
        ...editedPrompt,
        updatedAt: new Date()?.toISOString()
      });
      onClose();
    } catch (error) {
      console.error('Error saving prompt:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddTag = () => {
    const tag = tagInput?.trim()?.toLowerCase();
    if (tag && !editedPrompt?.tags?.includes(tag)) {
      setEditedPrompt(prev => ({
        ...prev,
        tags: [...prev?.tags, tag]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setEditedPrompt(prev => ({
      ...prev,
      tags: prev?.tags?.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      handleAddTag();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-modal w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-text-primary">Edit Prompt</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Prompt Content */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Prompt Content
              </label>
              <textarea
                value={editedPrompt?.content}
                onChange={(e) => setEditedPrompt(prev => ({ ...prev, content: e?.target?.value }))}
                className="w-full h-32 p-3 border border-border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter your prompt content..."
              />
            </div>

            {/* Category */}
            <div>
              <Select
                label="Category"
                options={categoryOptions}
                value={editedPrompt?.category}
                onChange={(value) => setEditedPrompt(prev => ({ ...prev, category: value }))}
                placeholder="Select a category"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Tags
              </label>
              <div className="flex items-center space-x-2 mb-3">
                <Input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e?.target?.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Add a tag..."
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddTag}
                  disabled={!tagInput?.trim()}
                  iconName="Plus"
                  iconSize={14}
                >
                  Add
                </Button>
              </div>
              
              {/* Tag List */}
              {editedPrompt?.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {editedPrompt?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-muted text-text-secondary text-sm rounded-full"
                    >
                      #{tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 hover:text-error transition-colors"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            loading={isSaving}
            disabled={!editedPrompt?.content?.trim()}
            iconName="Save"
            iconSize={16}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditPromptModal;